import { Star } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { cn } from '@/lib/shadcn/utils';
import { useSubmitPrescreenResultMutation } from '@/modules/prescreen/mutations/useSubmitPrescreenResultMutation';
import { Button } from '@/modules/ui/components/button/Button';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';

interface PrescreenSatisfactionScoreProps {
  candidateJobId: string;
  jobId: string;
  onComplete: () => void;
}

type SubmissionState = 'idle' | 'reasons';

const scores = [1, 2, 3, 4, 5] as const;

const SCORE_LABELS: Record<(typeof scores)[number], string> = {
  1: 'Terrible',
  2: 'Poor',
  3: 'Okay',
  4: 'Good',
  5: 'Excellent',
};

const POSITIVE_THRESHOLD = 4;

const POSITIVE_REASONS = [
  { id: 'felt_natural', label: 'Felt natural' },
  { id: 'good_questions', label: 'Good questions' },
  { id: 'quick_and_easy', label: 'Quick & easy' },
] as const;

const NEGATIVE_REASONS = [
  { id: 'hallucinated', label: 'Agent hallucinated' },
  { id: 'confusing_questions', label: 'Confusing questions' },
  { id: 'technical_issues', label: 'Technical issues' },
] as const;

const OTHER_REASON_ID = 'other';

export function PrescreenSatisfactionScore({
  candidateJobId,
  jobId,
  onComplete,
}: PrescreenSatisfactionScoreProps) {
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [hoveredScore, setHoveredScore] = useState<number | null>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [otherNote, setOtherNote] = useState('');
  const [submissionState, setSubmissionState] =
    useState<SubmissionState>('idle');

  const submitPrescreenResultMutation = useSubmitPrescreenResultMutation();
  const trackEvent = useTrackEvent();

  const handleScoreClick = async (score: number) => {
    if (submitPrescreenResultMutation.isPending || submissionState !== 'idle') {
      return;
    }

    setSelectedScore(score);

    try {
      await submitPrescreenResultMutation.mutateAsync({
        candidateJobId,
        satisfactionScore: score,
      });
      setSubmissionState('reasons');
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'We could not save your rating.',
      );
      onComplete();
    }
  };

  const isPositive =
    selectedScore !== null && selectedScore >= POSITIVE_THRESHOLD;
  const reasons = isPositive ? POSITIVE_REASONS : NEGATIVE_REASONS;

  const trimmedNote = otherNote.trim();
  const isOtherSelected = selectedReason === OTHER_REASON_ID;
  const canSubmitReason =
    selectedReason !== null && (!isOtherSelected || trimmedNote.length > 0);

  const handleSubmitReason = () => {
    if (!canSubmitReason || selectedScore === null) {
      return;
    }

    trackEvent('prescreenFeedbackSubmitted', {
      jobId,
      satisfactionScore: selectedScore,
      reason: selectedReason!,
      note: isOtherSelected ? trimmedNote : undefined,
    });
    onComplete();
  };

  const handleSkipReason = () => {
    onComplete();
  };

  if (submissionState === 'reasons') {
    return (
      <div className="bg-card rounded-xl border p-5 text-center">
        <p className="mb-2 text-sm font-medium">
          {isPositive
            ? 'Awesome! What stood out?'
            : 'We hear you! What got in the way?'}
        </p>
        <Paragraph variant="caption" className="text-muted-foreground mb-4">
          Your answer helps us improve the voice intro for future candidates.
        </Paragraph>

        <div
          className="mb-3 flex flex-wrap justify-center gap-2"
          role="radiogroup"
          aria-label="Feedback reason"
        >
          {reasons.map((reason) => {
            const isSelected = selectedReason === reason.id;
            return (
              <button
                key={reason.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => setSelectedReason(reason.id)}
                className={cn(
                  'focus-visible:ring-primary/40 rounded-full border px-3 py-1.5 text-sm transition-all duration-150 ease-out focus-visible:ring-2 focus-visible:outline-none',
                  isSelected
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {reason.label}
              </button>
            );
          })}
          <button
            type="button"
            role="radio"
            aria-checked={isOtherSelected}
            onClick={() => setSelectedReason(OTHER_REASON_ID)}
            className={cn(
              'focus-visible:ring-primary/40 rounded-full border px-3 py-1.5 text-sm transition-all duration-150 ease-out focus-visible:ring-2 focus-visible:outline-none',
              isOtherSelected
                ? 'border-primary bg-primary/10 text-primary font-medium'
                : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            Other
          </button>
        </div>

        {isOtherSelected && (
          <Textarea
            value={otherNote}
            onChange={(event) => setOtherNote(event.target.value)}
            placeholder={
              isPositive
                ? 'What made the voice intro work for you?'
                : 'What would have made this better?'
            }
            rows={3}
            maxLength={500}
            autoFocus
            className="mb-3 text-left"
            aria-label="Other feedback"
          />
        )}

        <div className="flex flex-col items-center gap-3">
          <Button
            variant="primary"
            onClick={handleSubmitReason}
            disabled={!canSubmitReason}
          >
            Share my feedback
          </Button>
          <button
            type="button"
            onClick={handleSkipReason}
            className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 transition-colors duration-150 hover:underline"
          >
            Skip feedback
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border p-5 text-center">
      <p className="mb-2 text-sm font-medium">
        How satisfied were you with the voice intro?
      </p>
      <Paragraph
        variant="caption"
        className="text-muted-foreground mb-4 min-h-5"
      >
        {(() => {
          const previewScore = hoveredScore ?? selectedScore;
          if (previewScore === null) {
            return 'Pick a rating.';
          }
          const label =
            SCORE_LABELS[previewScore as (typeof scores)[number]] ?? '';
          return <span className="text-foreground font-medium">{label}</span>;
        })()}
      </Paragraph>

      <div
        className="mb-3 flex flex-wrap justify-center gap-1"
        role="group"
        aria-label="Voice intro satisfaction score"
        onMouseLeave={() => setHoveredScore(null)}
      >
        {scores.map((score) => {
          const previewScore = hoveredScore ?? selectedScore;
          const isActive =
            previewScore !== null ? score <= previewScore : false;

          return (
            <button
              key={score}
              type="button"
              className={cn(
                'focus-visible:ring-primary/40 rounded-full p-2 transition-transform duration-150 ease-out hover:scale-110 focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60',
                submitPrescreenResultMutation.isPending &&
                  'pointer-events-none',
              )}
              onClick={() => handleScoreClick(score)}
              onMouseEnter={() => setHoveredScore(score)}
              onFocus={() => setHoveredScore(score)}
              onBlur={() => setHoveredScore(null)}
              disabled={submitPrescreenResultMutation.isPending}
              aria-label={`Rate this voice intro ${score} out of 5 — ${SCORE_LABELS[score]}`}
            >
              <Star
                className={cn(
                  'h-7 w-7 transition-colors duration-150',
                  isActive
                    ? 'fill-semantic-540 text-semantic-540'
                    : 'text-muted-foreground/40',
                )}
              />
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={handleSkipReason}
        className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 transition-colors duration-150 hover:underline"
      >
        Skip feedback
      </button>
    </div>
  );
}
